import { useCallback, useEffect, useRef, useState } from 'react';
import { getRequiredDocuments, getWritingTypeDocuments, setWritingTypeDocuments } from 'services/escriturasTemplatesService';
import DocumentCatalogModal from './DocumentCatalogModal';
import './escriturasTemplates.css';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) => (e && typeof e === 'object' && 'target' in e ? e.target.value : e);

/** Llave de unicidad de una fila: documento + rol normalizado (trim + MAYÚSCULAS). */
const rowKey = (docId, role) => `${docId}|${(role || '').trim().toUpperCase()}`;

// Estilos de la tabla compacta (diseño "Documentos requeridos - Tabla").
const CARD = { background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(16,24,40,.08),0 0 0 1px rgba(16,24,40,.06)', overflow: 'visible' };
const GRID = { display: 'grid', gridTemplateColumns: '28px 1fr 200px 36px', gap: 10, alignItems: 'center' };
const HDR = { fontSize: 11, fontWeight: 600, color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '.04em' };
const NUM = { fontSize: 12, color: '#98a2b3', textAlign: 'right' };

/**
 * Botón "+ Agregar documento" que despliega un menú con buscador (no convierte el
 * botón en un input; el botón sigue siendo botón y abre el panel). Picar una opción
 * agrega la fila; el pie del menú permite crear un documento nuevo en el catálogo.
 */
function AddDocumentMenu({ Input, Button, options, onPick, onCreate, t }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  const needle = q.trim().toLowerCase();
  const filtered = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;

  const itemStyle = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '8px 14px', fontSize: 14, color: '#1e293b', cursor: 'pointer' };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {Button && <Button label={t('tpl_typedocs_add')} size='sm' variant='naked' onClick={() => { setQ(''); setOpen((o) => !o); }} />}
      {open && (
        <div style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, marginTop: 6, width: 'min(440px, 92vw)', background: '#fff', borderRadius: 8, boxShadow: '0 8px 28px rgba(16,24,40,.16),0 0 0 1px rgba(16,24,40,.08)' }}>
          <div style={{ padding: 8, borderBottom: '1px solid #eef0f3' }}>
            {Input
              ? <Input icon='search' value={q} onChange={(e) => setQ(readVal(e))} placeholder={t('tpl_typedocs_search')} width='100%' autoFocus />
              : <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('tpl_typedocs_search')} style={{ width: '100%', padding: '7px 10px' }} />}
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', padding: '4px 0' }}>
            {filtered.length === 0 && <div style={{ padding: '8px 14px', fontSize: 13, color: '#98a2b3' }}>{t('tpl_typedocs_search_empty')}</div>}
            {filtered.map((o) => (
              <button
                key={o.value}
                type='button'
                style={itemStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                onClick={() => { onPick(o.value); setOpen(false); }}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #eef0f3', padding: 4 }}>
            <button
              type='button'
              style={{ ...itemStyle, fontSize: 13, color: '#1565c0', fontWeight: 500 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              onClick={() => { setOpen(false); onCreate(); }}
            >
              {t('tpl_typedocs_new_doc')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tab "Documentos requeridos" del detalle: tabla compacta que asigna a un tipo de
 * escritura los documentos del catálogo (rol opcional, texto libre). Agregar abre un
 * menú con buscador; "crear documento nuevo" abre el catálogo como overlay sin perder
 * los cambios sin guardar. Guardar reemplaza el set completo (PUT) y valida que no se
 * repita documento + rol.
 */
export default function WritingTypeDocumentsPanel({ DesignSystemComponents, writingTypeId, t, onDirtyChange }) {
  const { Button, IconButton, Input, Skeleton } = DesignSystemComponents ?? {};

  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]); // [{ uid, requiredDocumentId, name, source, roleQualifier }]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | done | error
  const [showCatalog, setShowCatalog] = useState(false);
  const [dirty, setDirty] = useState(false);
  const uidRef = useRef(0);
  const nextUid = () => { uidRef.current += 1; return uidRef.current; };
  const submittingRef = useRef(false);
  const abortRef = useRef(null);

  // El detalle usa esto para advertir "cambios sin guardar" al salir/cambiar de tab.
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  // Al desmontar (cambio de tab/recarga), limpia el flag para no dejar un guard fantasma.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const fetchCatalog = useCallback(async (signal) => {
    const cat = await getRequiredDocuments(false, signal);
    setCatalog(cat || []);
  }, []);

  const fetchAll = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const [, assigned] = await Promise.all([
        fetchCatalog(controller.signal),
        getWritingTypeDocuments(writingTypeId, controller.signal),
      ]);
      setRows((assigned || []).map((a) => ({
        uid: nextUid(),
        requiredDocumentId: a.requiredDocumentId,
        name: a.name,
        source: a.sourceAlias,
        roleQualifier: a.roleQualifier || '',
      })));
      setDirty(false);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.response?.data?.message || err.message || t('tpl_error_generic'));
    } finally {
      setLoading(false);
    }
  }, [writingTypeId, fetchCatalog, t]);

  useEffect(() => {
    fetchAll();
    return () => abortRef.current?.abort();
  }, [fetchAll]);

  const addOptions = catalog.map((c) => ({
    label: c.sourceAlias ? `${c.name} · ${c.sourceAlias}` : c.name,
    value: c.id,
  }));

  // Índices de filas duplicadas (mismo doc + rol) para marcarlas y bloquear el guardado.
  const dupIndexes = (() => {
    const seen = new Map();
    const dups = new Set();
    rows.forEach((r, i) => {
      const k = rowKey(r.requiredDocumentId, r.roleQualifier);
      if (seen.has(k)) { dups.add(i); dups.add(seen.get(k)); } else seen.set(k, i);
    });
    return dups;
  })();

  const onPick = (id) => {
    const c = catalog.find((d) => d.id === id);
    if (!c) return;
    setRows((prev) => [...prev, { uid: nextUid(), requiredDocumentId: c.id, name: c.name, source: c.sourceAlias, roleQualifier: '' }]);
    setSaveStatus('idle'); setDirty(true);
  };
  const removeRow = (uid) => { setRows((prev) => prev.filter((r) => r.uid !== uid)); setSaveStatus('idle'); setDirty(true); };
  const setRowRole = (uid, role) => { setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, roleQualifier: role } : r))); setSaveStatus('idle'); setDirty(true); };

  const handleSave = async () => {
    if (submittingRef.current) return;
    if (dupIndexes.size > 0) { setError(t('tpl_typedocs_dup')); setSaveStatus('error'); return; }
    submittingRef.current = true;
    setSaveStatus('saving');
    setError(null);
    try {
      const items = rows.map((r) => ({ requiredDocumentId: r.requiredDocumentId, roleQualifier: r.roleQualifier.trim() || null }));
      const saved = await setWritingTypeDocuments(writingTypeId, items);
      setRows((saved || []).map((a) => ({ uid: nextUid(), requiredDocumentId: a.requiredDocumentId, name: a.name, source: a.sourceAlias, roleQualifier: a.roleQualifier || '' })));
      setSaveStatus('done'); setDirty(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('tpl_error_generic'));
      setSaveStatus('error');
    } finally {
      submittingRef.current = false;
    }
  };

  const saving = saveStatus === 'saving';
  const saveLabel = saving ? t('tpl_save_saving') : saveStatus === 'done' ? t('tpl_save_done') : t('tpl_typedocs_save');

  // Skeleton de carga: cabecera + 4 filas.
  const skeletonView = (
    <div style={CARD}>
      <div style={{ padding: '13px 16px', borderBottom: '1px solid #eef0f3' }}>
        {Skeleton ? <Skeleton height='1rem' width='38%' /> : <span className='font-light'>{t('tpl_loading')}</span>}
      </div>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{ ...GRID, padding: '12px 16px', borderTop: '1px solid #f4f5f7' }}>
          <span />
          {Skeleton ? <Skeleton height='1rem' width='80%' /> : <span />}
          {Skeleton ? <Skeleton height='2rem' width='100%' /> : <span />}
          <span />
        </div>
      ))}
    </div>
  );

  return (
    <div className='wtd-panel' style={{ width: '100%' }}>
      <div className='d-flex justify-content-between align-items-start gap-2' style={{ marginBottom: 16 }}>
        <p className='caption font-light mb-0'>{t('tpl_typedocs_subtitle')}</p>
        {Button && <Button label={t('tpl_doc_catalog_btn')} size='sm' variant='secondary' onClick={() => setShowCatalog(true)} />}
      </div>
      {error && <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>{error}</div>}

      {loading ? (
        skeletonView
      ) : catalog.length === 0 ? (
        <div style={CARD}>
          <div style={{ padding: 16 }}>
            <p className='caption font-light mb-2'>{t('tpl_typedocs_no_catalog')}</p>
            {Button && <Button label={t('tpl_typedocs_new_doc')} size='sm' variant='primary' onClick={() => setShowCatalog(true)} />}
          </div>
        </div>
      ) : (
        <div style={CARD}>
          {/* Cabecera con conteo */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: '1px solid #eef0f3' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#344054' }}>{t('tpl_typedocs_action')}</span>
            <span style={{ fontSize: 12, color: '#667085', background: '#f2f4f7', padding: '2px 9px', borderRadius: 999 }}>{rows.length}</span>
          </div>

          {/* Encabezados de columna */}
          <div style={{ ...GRID, padding: '9px 16px 4px' }}>
            <span />
            <span style={HDR}>{t('tpl_typedocs_col_doc')}</span>
            <span style={HDR}>{t('tpl_typedocs_col_role')}</span>
            <span />
          </div>

          {/* Filas */}
          {rows.length === 0 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #f4f5f7' }} className='caption font-light'>{t('tpl_typedocs_empty')}</div>
          )}
          {rows.map((row, idx) => (
            <div
              key={row.uid}
              style={{ ...GRID, padding: '8px 16px', borderTop: '1px solid #f4f5f7', background: dupIndexes.has(idx) ? 'var(--error-100, #fee2e2)' : undefined }}
            >
              <span style={NUM}>{idx + 1}</span>
              <span style={{ fontSize: 14, lineHeight: 1.3 }}>
                {row.name}
                {row.source && <span style={{ color: '#aab1bd', fontSize: 13 }}> · {row.source}</span>}
              </span>
              {Input && (
                <Input
                  value={row.roleQualifier}
                  onChange={(e) => setRowRole(row.uid, readVal(e))}
                  placeholder={t('tpl_typedocs_role_placeholder')}
                  disabled={saving}
                  width='100%'
                />
              )}
              {IconButton
                ? <IconButton ariaLabel={t('tpl_typedocs_remove')} icon='close' size='sm' stroke='var(--error-500)' onClick={() => removeRow(row.uid)} />
                : <button type='button' className='btn btn-link btn-sm text-danger' onClick={() => removeRow(row.uid)} title={t('tpl_typedocs_remove')}>✕</button>}
            </div>
          ))}

          {/* Agregar: botón que despliega un menú con buscador */}
          <div style={{ padding: '11px 16px 14px', borderTop: '1px solid #f4f5f7' }}>
            <AddDocumentMenu
              Input={Input}
              Button={Button}
              options={addOptions}
              onPick={onPick}
              onCreate={() => setShowCatalog(true)}
              t={t}
            />
          </div>
        </div>
      )}

      {/* Guardar */}
      <div className='d-flex justify-content-end gap-2 mt-3 align-items-center'>
        {saveStatus === 'done' && <span style={{ color: 'var(--success-500, #16a34a)', fontSize: 13 }}>✓</span>}
        {Button && <Button label={saveLabel} size='sm' variant='primary' onClick={handleSave} disabled={saving || loading} />}
      </div>

      {/* Catálogo como overlay: NO desmonta el panel → no se pierden los cambios sin guardar.
          Al cerrar solo se refresca la lista del catálogo (para que aparezca lo recién creado). */}
      {showCatalog && (
        <DocumentCatalogModal
          DesignSystemComponents={DesignSystemComponents}
          t={t}
          onClose={() => { setShowCatalog(false); fetchCatalog().catch(() => {}); }}
        />
      )}
    </div>
  );
}
